<template>
  <popup :close-action="closeActionClicked" :buttons="buttons" class="subscription-cta-popup">
    <template v-slot:title>
      <div class="title-content">
        <component v-bind:is="contentTemplateByTrialPeriod" />
        <div class="trial-period-text">
          <span class="days-left" :class="periodVariant">{{ daysLeftText }}</span>
          <span> לתקופת הניסיון</span>
        </div>
        <div class="progress-bar-container">
          <simple-progress-bar :variant="periodVariant" :progressInPercentage="trialProgressInPercentage" @progressBarClicked="progressBarClicked"/>
        </div>
        <dates-path :since="since" :until="until"/>
      </div>
    </template>
    <template v-slot:content>
      <div class="content-content">
        <div v-if="oneMonthFree" class="discount">
          <div class="gift">🎁 מגיע לך חודש נוסף ללא חיוב מתנה!</div>
          כדי להמשיך וליהנות מהשירות גם לאחר תקופת הניסיון ולנצל את ההטבה אפשר להקליק ולעבור למסך פרטי התשלום.
        </div>
        <div v-else>
          כדי להמשיך וליהנות מהשירות גם אחרי סיום תקופת הניסיון, כדאי לעדכן את פרטי התשלום כאן.
        </div>
      </div>
    </template>
  </popup>
</template>

<script>
import BaseUI from 'base-ui';
import { mapActions, mapGetters, mapState } from 'vuex';
import DatesPath from './DatesPath.vue';
import SimpleProgressBar from './SimpleProgressBar.vue';
import router from '../../../../router';
import Segment from '../../../../Segment';
import { TRIAL_PERIODS } from '../../../../constants/subscription-state';
import BeginningPeriodContent from './variants/BeginningPeriodContent.vue';
import MiddlePeriodContent from './variants/MiddlePeriodContent.vue';
import FinalPeriodContent from './variants/FinalPeriodContent.vue';
import trialUtils from './trial-utils';

export default {
  name: 'SubscriptionCtaPopup',
  components: {
    SimpleProgressBar,
    DatesPath,
    BeginningPeriodContent,
    MiddlePeriodContent,
    FinalPeriodContent,
    Popup: BaseUI.Popup,
  },
  data() {
    return {
      PERIOD_TO_CONTENT_MAPPING: {
        [TRIAL_PERIODS.BEGINNING]: BeginningPeriodContent,
        [TRIAL_PERIODS.MIDDLE]: MiddlePeriodContent,
        [TRIAL_PERIODS.FINAL]: FinalPeriodContent,
      },
    };
  },
  computed: {
    ...mapState('subscriptionState', ['since', 'until']),
    ...mapGetters('subscriptionState', ['daysLeft', 'trialLength', 'trialPeriod']),
    ...mapState('discountPromotion', ['eligibleForDiscountPromotion']),
    ...mapState('benefits', ['benefitType', 'benefitAmount']),
    ...mapGetters('benefits', ['BENEFIT_TYPES']),
    // TODO - make this more generic
    oneMonthFree() {
      return this.eligibleForDiscountPromotion || (this.benefitType === this.BENEFIT_TYPES.FREE_MONTHS && this.benefitAmount === 1);
    },
    periodVariant() {
      return trialUtils.periodVariant(this.trialPeriod);
    },
    contentTemplateByTrialPeriod() {
      return this.PERIOD_TO_CONTENT_MAPPING[this.trialPeriod];
    },
    daysLeftText() {
      const humanizedDays = trialUtils.humanizeDays(this.daysLeft);
      return humanizedDays || 'תקופת הניסיון הסתיימה';
    },
    trialProgressInPercentage() {
      return (this.daysLeft / this.trialLength) * 100;
    },
    buttons() {
      const cta = this.oneMonthFree ? 'להרשמה וקבלת ההטבה' : 'להרשמה עם כרטיס אשראי';
      return [{
        title: cta,
        action: () => {
          Segment.trackUserInteraction('SubscriptionCtaPopup_SubscribeButtonClicked', { trialPeriod: this.trialPeriod });
          this.$emit('close');
          router.push({ path: '/subscription' });
        },
        variant: 'primary',
      },
      {
        title: 'תזכירו לי בפעם אחרת',
        action: this.dismissButtonClicked,
        variant: 'secondary',
      }];
    },
  },
  created() {
    Segment.trackUserInteraction('SubscriptionCtaPopupShown', { trialPeriod: this.trialPeriod });
  },
  methods: {
    ...mapActions('subscriptionState', ['ctaDismissed']),
    closeActionClicked() {
      Segment.trackUserInteraction('SubscriptionCtaPopup_CloseButtonClicked', { trialPeriod: this.trialPeriod });
      this.subscriptionCtaDismissed();
    },
    dismissButtonClicked() {
      Segment.trackUserInteraction('SubscriptionCtaPopup_DismissButtonClicked', { trialPeriod: this.trialPeriod });
      this.subscriptionCtaDismissed();
    },
    subscriptionCtaDismissed() {
      this.ctaDismissed();
      this.$emit('close');
    },
    progressBarClicked() {
      Segment.trackUserInteraction('SubscriptionCtaPopup_ProgressBarClicked', { trialPeriod: this.trialPeriod });
    },
  },
};
</script>

<style scoped lang="scss">
  @import '~base-ui/src/scss/riseup-colors';

  .subscription-cta-popup {

    .title-content {
      font-family: SimplerPro;
      font-size: 14px;

      .trial-period-text {
        margin-top: 32px;
        margin-bottom: 16px;
        font-size: 16px;
        font-family: SimplerPro,sans-serif;

        .days-left  {
          font-weight: bold;

          &.warning {
            color: $riseup_mustard;
          }

          &.danger {
            color: $riseup_coral;
          }
        }
      }

      .dates-path {
        margin-top: 3px;
      }

      .progress-bar-container {
        position: relative;
        height: 27px;

        .simple-progress-bar {
          position: absolute;
          left: -11px;
          width: 110%;
        }
      }
    }

    .content-content {
      margin-top: 30px;
      line-height: 1.5;

      .discount {
        font-size: 14px;

        .gift {
          font-weight: bold;
          margin-bottom: 10px;
        }
      }
    }

  }

</style>
